import { useTokenPriceState } from '@pancakeswap/utils/useCakePrice'
import useSWR from 'swr'
import { useContractReads } from 'wagmi'
import { V4_ADDRESS, V4_API_URL, V4_CHAIN_ID } from 'views/TradingCompetitionV4/config'
import type { BoardResponse } from './server'
import { CUR_INDEX, Cur, parseConfig } from './logic'

/** Views of the V4 contract the Earn section reads (one multicall). The only write it sends is withdrawReferralCredit (competitionV4Abi). */
export const EARN_VIEWS_ABI = [
  { type: 'function', name: 'referralCredit', stateMutability: 'view', inputs: [{ type: 'uint8' }, { type: 'address' }], outputs: [{ type: 'uint256' }] },
  { type: 'function', name: 'referralTotalEarned', stateMutability: 'view', inputs: [{ type: 'uint8' }, { type: 'address' }], outputs: [{ type: 'uint256' }] },
  { type: 'function', name: 'referralPaidCount', stateMutability: 'view', inputs: [{ type: 'uint8' }, { type: 'address' }], outputs: [{ type: 'uint256' }] },
  { type: 'function', name: 'referrerRegistered', stateMutability: 'view', inputs: [{ type: 'address' }], outputs: [{ type: 'bool' }] },
  { type: 'function', name: 'referralOverrideSet', stateMutability: 'view', inputs: [{ type: 'address' }], outputs: [{ type: 'bool' }] },
  { type: 'function', name: 'referralOverrideBps', stateMutability: 'view', inputs: [{ type: 'address' }], outputs: [{ type: 'uint16' }] },
] as const

const getJson = (u: string) => fetch(u).then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))

/**
 * Every programme figure (fees, discount, tier rates) from the public config, which the API reads from the contract
 * (GET /api/competitions/v4/config). Tiers it has not read yet come as null and the page shows "—", never a default.
 */
export function useEarnConfig() {
  const { data, error } = useSWR(V4_ADDRESS ? `${V4_API_URL}/config` : null, getJson, { revalidateOnFocus: false, dedupingInterval: 60_000, refreshInterval: 300_000 })
  return { config: parseConfig(data), loading: !data && !error, failed: Boolean(error) && !data }
}

export interface ChainEarn {
  credit: Record<Cur, bigint | undefined>
  earned: Record<Cur, bigint | undefined>
  count: Record<Cur, number | undefined>
  registered: boolean | undefined
  override: { set: boolean; bps: number } | null | undefined
}

/** The wallet's own referral state, read from the V4 contract in ONE multicall (the programme's rates come from the config). */
export function useEarnChain(wallet?: string) {
  const on = (functionName: string, args: unknown[]) => ({ address: (V4_ADDRESS || undefined) as `0x${string}` | undefined, abi: EARN_VIEWS_ABI, functionName, args, chainId: V4_CHAIN_ID })
  const w = wallet as `0x${string}` | undefined
  const walletReads = w
    ? [
        on('referralCredit', [0, w]),
        on('referralCredit', [1, w]),
        on('referralTotalEarned', [0, w]),
        on('referralTotalEarned', [1, w]),
        on('referralPaidCount', [0, w]),
        on('referralPaidCount', [1, w]),
        on('referrerRegistered', [w]),
        on('referralOverrideSet', [w]),
        on('referralOverrideBps', [w]),
      ]
    : []
  const { data, isLoading, refetch } = useContractReads({
    contracts: walletReads as any,
    enabled: Boolean(V4_ADDRESS && w),
    allowFailure: true,
    staleTime: 15_000,
    watch: false,
  }) as any
  const v = (i: number) => (data?.[i]?.status === 'success' ? data[i].result : undefined)
  const W0 = 0
  const num = (x: unknown) => (typeof x === 'bigint' ? Number(x) : undefined)
  const overrideSet = v(W0 + 7) as boolean | undefined
  const chain: ChainEarn = {
    credit: { bnb: v(W0) as bigint | undefined, cbon: v(W0 + 1) as bigint | undefined },
    earned: { bnb: v(W0 + 2) as bigint | undefined, cbon: v(W0 + 3) as bigint | undefined },
    count: { bnb: num(v(W0 + 4)), cbon: num(v(W0 + 5)) },
    registered: v(W0 + 6) as boolean | undefined,
    override: overrideSet === undefined ? undefined : overrideSet ? { set: true, bps: Number(v(W0 + 8) ?? 0) } : null,
  }
  return { chain, loading: isLoading, refetch }
}

/** The top affiliates and this wallet's credited competitions (GET /api/earn/board, one request, kept a minute). */
export function useEarnBoard(wallet?: string) {
  const { data, error } = useSWR<BoardResponse>(V4_ADDRESS ? `/api/earn/board${wallet ? `?wallet=${wallet.toLowerCase()}` : ''}` : null, getJson, {
    revalidateOnFocus: false,
    dedupingInterval: 60_000,
    refreshInterval: 120_000,
    keepPreviousData: true,
  })
  return { board: data, loading: !data && !error, failed: Boolean(error) && !data }
}

/** USD prices of the two fee currencies: the app's own price API (the same numbers as the token pages). */
export function useFeePrices(): Record<Cur, number | null> {
  const bnb = useTokenPriceState('bnb')
  const cbon = useTokenPriceState('cbon')
  const val = (s: ReturnType<typeof useTokenPriceState>) => (s.price && (s.status === 'ready' || s.status === 'stale') ? Number(s.price.toString()) : null)
  return { bnb: val(bnb), cbon: val(cbon) }
}

export { CUR_INDEX }
